if (!customElements.get('quick-add-modal')) {
  class QuickAddModal extends HTMLElement {
    constructor() {
      super();
      this.modalContent = this.querySelector('.quick-add-modal__content-info');
    }

    connectedCallback() {
      // connectedCallback re-fires every time the element is re-inserted into
      // the DOM. moveModalOnScreenSizeChange() relocates modals to/from
      // document.body on mobile load and breakpoint changes, so guard listener
      // attachment with a one-time flag to avoid stacking duplicate handlers.
      if (!this.listenersAttached) {
        this.listenersAttached = true;

        const toggleElement = this.querySelector('.quick-add-modal__toggle');

        toggleElement.addEventListener('click', function () {
          this.hide();
          this.style = null;
        }.bind(this));

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
        this.addEventListener('click', (event) => {
          if (event.target === this) this.hide();
        });
      }

      // Register this modal for responsive handling (registeredModals is a Set,
      // so re-registering on reconnect is a no-op).
      QuickAddModal.registerModal(this);
    }

    // Static method to register modals for batch processing
    static registerModal(modal) {
      if (!QuickAddModal.registeredModals) {
        QuickAddModal.registeredModals = new Set();
        QuickAddModal.currentBreakpoint = window.matchMedia('(min-width: 768px)').matches ? 'desktop' : 'mobile';
        QuickAddModal.initializeResponsiveHandler();
      }
      QuickAddModal.registeredModals.add(modal);
      QuickAddModal.moveModalOnScreenSizeChange();
    }

    // Static method to unregister modal when it's removed
    static unregisterModal(modal) {
      if (QuickAddModal.registeredModals) {
        QuickAddModal.registeredModals.delete(modal);
      }
    }

    // Single global responsive handler
    static initializeResponsiveHandler() {
      const mediaQuery = window.matchMedia('(min-width: 768px)');
      
      const handleBreakpointChange = (e) => {
        const newBreakpoint = e.matches ? 'desktop' : 'mobile';
        
        // Only process if breakpoint actually changed
        if (QuickAddModal.currentBreakpoint !== newBreakpoint) {
          QuickAddModal.currentBreakpoint = newBreakpoint;
          
          // Use requestAnimationFrame to batch DOM updates
          requestAnimationFrame(() => {
            QuickAddModal.moveModalOnScreenSizeChange();
          });
        }
      };

      mediaQuery.addEventListener('change', handleBreakpointChange);
    }


    // Method to move modals to document.body for fixed positioning
    static moveModalOnScreenSizeChange() {
      // Guard against re-entrancy: the appendChild calls below move a connected
      // custom element, which synchronously re-fires disconnectedCallback +
      // connectedCallback -> registerModal -> this method. Without this guard
      // that recurses once per modal on the page until the call stack overflows
      // (RangeError). The outer forEach still moves every modal in one pass.
      if (QuickAddModal.isMovingModals) return;
      QuickAddModal.isMovingModals = true;

      try {
        const isDesktop = window.matchMedia('(min-width: 768px)').matches;
        const modalElements = document.querySelectorAll('quick-add-modal');

        modalElements.forEach(modal => {
          if (!isDesktop) {
            // Mobile: move to document.body for fixed positioning
            if (modal.parentNode !== document.body) {
              modal.originalParent = modal.parentNode;
              document.body.appendChild(modal);
            }
          } else {
            // Desktop: move back to original parent (card wrapper) for absolute positioning
            if (modal.originalParent && modal.parentNode !== modal.originalParent) {
              modal.originalParent.appendChild(modal);
              delete modal.originalParent;
            }
          }
        });
      } finally {
        QuickAddModal.isMovingModals = false;
      }
    }

    disconnectedCallback() {
      // Clean up when modal is removed from DOM
      QuickAddModal.unregisterModal(this);
    }

    show(opener) {
      this.openedBy = opener;
      this.style.opacity = '0';
      this.style.visibility = 'hidden';
      this.setAttribute('open', '');

      // Move modal to document.body for mobile to ensure fixed positioning works
      // For desktop, keep modal in card wrapper for absolute positioning
      const isDesktop = window.matchMedia('(min-width: 768px)').matches;
      if (!isDesktop && this.parentNode !== document.body) {
        this.originalParent = this.parentNode;
        document.body.appendChild(this);
      }

      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading__spinner').classList.remove('hidden');

      fetch(opener.getAttribute('data-product-url'))
        .then((response) => response.text())
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');

          this.productElement.classList.forEach((classApplied) => {
            if (classApplied.startsWith('color-') || classApplied === 'gradient')
              this.modalContent.classList.add(classApplied);
          });

          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.preventVariantURLSwitching();

          const variantPicker = this.modalContent.querySelector('variant-selects');
          if (!variantPicker) return;
    
          // Listen to touchstart and click events
          const boundHandler = this.handleMobileTap.bind(this);
          const variantInputsAndLabels = variantPicker.querySelectorAll('input[type="radio"], label[for]');
          variantInputsAndLabels.forEach(element => {
            element.addEventListener('touchstart', boundHandler, { passive: false });
            element.addEventListener('click', boundHandler);
          });

          // Track size selection
          var titleEl = this.modalContent.querySelector('.product__title-heading');
          var productTitle = titleEl ? titleEl.textContent.trim() : '';
          variantPicker.querySelectorAll('input[type="radio"]').forEach(input => {
            input.addEventListener('change', () => typeof send === 'function' && send('quick_add_size_select', { product_title: productTitle }));
          });

        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading__spinner').classList.add('hidden');
          this.style.opacity = '1';
          this.style.visibility = 'visible';
        });


      if (window.matchMedia('(max-width: 767px)').matches) {
        document.body.classList.add('overflow-hidden');
      }

      trapFocus(this, this.querySelector('[role="dialog"]'));
      window.pauseAllMedia();
    }

    hide() {

      if (window.matchMedia('(max-width: 767px)').matches) {
        this.querySelector('.quick-add-modal__content').style.transform = 'translateY(100%)';
      }

      setTimeout(() => {
        this.removeAttribute('open');
        
        // Move modal back to original parent if it was moved to document.body
        if (this.originalParent && this.parentNode === document.body) {
          this.originalParent.appendChild(this);
          delete this.originalParent;
        }

        this.querySelector('.quick-add-modal__content').style.transform = '';
        this.modalContent.innerHTML = '';
        
        if (this.openedBy) {
          this.openedBy = null;
        }
        // Unlock the body only if the screen size is mobile
        if (window.matchMedia('(max-width: 767px)').matches) {
          document.body.classList.remove('overflow-hidden');
        }
        document.body.dispatchEvent(new CustomEvent('modalClosed'));
        removeTrapFocus(this.openedBy);
        window.pauseAllMedia();
        this.style.visibility = '';
        this.style.opacity = '';
      }, 1000);
    }
    setInnerHTML(element, html) {
      element.innerHTML = html;

      element.querySelectorAll('script').forEach((oldScriptTag) => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach((attribute) => {
          newScriptTag.setAttribute(attribute.name, attribute.value);
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent.querySelector('variant-selects');
      if (!variantPicker) return;

      variantPicker.setAttribute('data-update-url', 'false');
    }

    handleMobileTap(event) {
      const variantPicker = this.modalContent.querySelector('variant-selects');
      if (!variantPicker) return;

      // Find the radio button from target (could be label, radio, or element inside label)
      const target = event.target;
      const label = target.tagName === 'LABEL' ? target : target.closest('label[for]');
      const radioToCheck = target.type === 'radio' ? target : 
                          (label ? variantPicker.querySelector(`#${label.getAttribute('for')}`) : null);
      
      if (!radioToCheck || radioToCheck.checked || radioToCheck.disabled) return;
      
      event.preventDefault();
      event.stopPropagation();
      
      var titleEl = this.modalContent.querySelector('.product__title-heading');
      if (typeof send === 'function') send('quick_add_size_select', {
        product_title: titleEl ? titleEl.textContent.trim() : ''
      });

      // Check the radio and uncheck others in the same group
      radioToCheck.checked = true;
      variantPicker.querySelectorAll(`input[type="radio"][name="${radioToCheck.name}"]`)
        .forEach(radio => { if (radio !== radioToCheck) radio.checked = false; });
      
      // Run the same variant refresh path used by standard radio changes.
      if (variantPicker.onVariantChange) {
        variantPicker.onVariantChange({ target: radioToCheck });
      }
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${sectionId}`);
      this.productElement.querySelectorAll('variant-selects, product-info').forEach((element) => {
        element.dataset.originalSection = sectionId;
      });
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      const modalDialog = this.productElement.querySelectorAll('modal-dialog');
      if (modalDialog) modalDialog.forEach((modal) => modal.remove());

      const sliderComponent = this.productElement.querySelector('slider-component');
      if (sliderComponent) sliderComponent.remove();

      const mediaGallery = this.productElement.querySelector('media-gallery');
      if (mediaGallery) mediaGallery.remove();

      /* Remove gifting sections for PLP quick shop (gift boxes only available on PDP) */
      this.productElement.querySelectorAll('.gifting-option-section, #selected-gifting-options, .gift_box-button').forEach(el => el.remove());
      this.productElement.querySelectorAll('input.gifting-property').forEach(el => el.remove());

      // Remove gifting flag attribute so product-form.js skips sanitiser
      this.productElement.removeAttribute('data-gifting');
    }
  }

  customElements.define('quick-add-modal', QuickAddModal);
}

class QuickAddOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');
    if (!button) return;
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const modalId = this.getAttribute('data-quick-add');
      const modal = document.querySelector(modalId);
      if (modal) {
        // Close all other modals before opening the new one
        document.querySelectorAll('quick-add-modal[open]').forEach(openModal => {
          if (openModal !== modal) {
            openModal.hide();
          }
        });
        modal.show(button);
        var cardHeading = button.closest('.product-card-wrapper')?.querySelector('.card__heading');
        if (typeof send === 'function') send('quick_add_start', {
          product_title: cardHeading ? cardHeading.textContent.trim() : ''
        });
      }
    });
  }
}

customElements.define('quick-add-opener', QuickAddOpener);
